<script setup lang="ts">
  import Checkbox from '@/components/form/Checkbox.vue'
  import { type Ref, ref, toRef, watch } from 'vue'
  import { belgium, checkVAT, netherlands } from 'jsvat-next'
  import {
    createOrganization,
    type CreateOrganization,
    type CreateOrganizationErrors,
    validateCreateOrganization,
  } from '@/model/create-organization.model.ts'
  import axios, { type AxiosResponse } from 'axios'
  import type { Organization } from '@/types/organization'
  import { useOrganizationStore } from '@/store/organization.store.ts'
  import { UiButton, Modal, FormInput, useToastStore } from '@logistiek-zonder-papier/lzp-library'
  import * as Sentry from '@sentry/browser'

  const show = defineModel<boolean>('show', { required: true })
  const { showCreateButton = false, owner = false } = defineProps<{
    showCreateButton?: boolean
    owner?: boolean
  }>()

  const organizationData = ref<CreateOrganization>(createOrganization(owner))
  const errors: Ref<CreateOrganizationErrors> = ref({})
  const submitAttempted = ref(false)
  const organizationStore = useOrganizationStore()
  const organizationNumber = toRef(() => organizationData.value.organizationNumber)
  const warning = ref<string | false>(false)
  const toastStore = useToastStore()

  const emit = defineEmits<{
    (e: 'created', organization: CreateOrganization): void
  }>()

  watch(
    () => organizationData.value,
    newVal => {
      errors.value = {}
      validateCreateOrganization(newVal, errors.value)
    },
    { deep: true, immediate: true }
  )

  async function submitNewOrganisation() {
    const isFormValid = validateForm(organizationData.value)

    if (!isFormValid && !validateCreateOrganization(organizationData.value)) return

    try {
      await organizationStore.createOrganization(organizationData.value)
    } catch (error) {
      toastStore.error('Er is een fout opgetreden bij het aanmaken van de organisatie')
      Sentry.captureException(error)
      return
    }
    show.value = false

    emit('created', organizationData.value)
    // Update the organization datatable organisaties.js listens to this event and triggers a reload of the datatable
    window.dispatchEvent(
      new CustomEvent('reload-dataTable', {
        detail: {
          success: true,
          message: 'De organisatie is succesvol aangemaakt.',
        },
      })
    )
  }
  // Typed (w)rapper, yo. Type object for reusability
  function typedEntries<T extends object>(obj: T) {
    return Object.entries(obj) as [keyof T, T[keyof T]][]
  }

  function validateForm(data: CreateOrganization) {
    const formItems = typedEntries(data).map(([key]) => {
      touched.value[key] = true
      return {
        key,
        error: fieldError(key),
      }
    })

    return formItems.every(value => value.error === undefined)
  }

  function fieldError(field: keyof CreateOrganization) {
    if (!submitAttempted.value && !touched.value[field]) return undefined
    return errors.value[field]
  }

  const touched = ref<Partial<Record<keyof CreateOrganization, boolean>>>({
    organizationName: false,
    organizationNumber: false,
    streetName: false,
    houseNumber: false,
    houseNumberSuffix: false,
    postalCode: false,
    city: false,
    country: false,
  })

  function markTouched(field: keyof CreateOrganization) {
    touched.value[field] = true
  }

  function checkVat() {
    const organizationNumber = organizationData.value.organizationNumber

    if (organizationNumber == null) return

    warning.value =
      organizationData.value.organizationNumber.length > 0 &&
      !checkVAT(organizationNumber, [netherlands, belgium]).isValid
        ? 'Ongeldig ondernemingsnummer'
        : false

    if (!checkVAT(organizationNumber, [netherlands, belgium]).isValid) {
      return
    }

    fetchOrganizationDetailsByBTW(organizationNumber)
  }

  watch(organizationNumber, checkVat)

  async function fetchOrganizationDetailsByBTW(btwNumber: string) {
    let result: AxiosResponse<Organization | null>
    try {
      result = await axios.get('/common/api/controle/organisatie/search?type=BTW', {
        params: {
          nummer: btwNumber,
        },
      })
    } catch (error) {
      warning.value = 'Ondernemingsnummer niet gevonden'
      Sentry.captureException(error)
      return // Ignore all failures
    }

    if (result.data == null) return

    // Todo - complain with the api team to fix their naming
    organizationData.value.organizationNumber = result.data.organisatieNummer ?? btwNumber
    organizationData.value.organizationName = result.data.organisatieNaam
    organizationData.value.streetName = result.data.straatnaam
    organizationData.value.houseNumber = Number(result.data.huisNummer)
    organizationData.value.postalCode = result.data.postcode
    organizationData.value.city = result.data.stad
    organizationData.value.country = result.data.land
    organizationData.value.isPrivateOrganization = false
  }

  // Reset form when modal is closed
  watch(show, newVal => {
    if (!newVal) {
      organizationData.value = createOrganization(owner)
      errors.value = {}
      submitAttempted.value = false
      warning.value = false
      markTouchedReset()
    }
  })

  function markTouchedReset() {
    touched.value = {
      organizationName: false,
      organizationNumber: false,
      streetName: false,
      houseNumber: false,
      houseNumberSuffix: false,
      postalCode: false,
      city: false,
      country: false,
    }
  }

  // warning.value is set in the checkVat and fetchOrganizationDetailsByBTW
  // future TODO - refactor this to be more elegant
  function fieldWarning(field: keyof CreateOrganization) {
    switch (field) {
      case 'organizationNumber':
        return warning.value !== false ? warning.value : undefined
      default:
        return undefined
    }
  }
</script>

<template>
  <UiButton
    v-if="showCreateButton"
    variant="prominent"
    label="Create Organization"
    @click="show = !show"
  />
  <Modal v-model:show="show">
    <template #header> Organisatie toevoegen</template>
    <FormInput
      v-model="organizationData.organizationName"
      label="Organisatienaam"
      placeholder="Vul organisatienaam in"
      :error="fieldError('organizationName')"
      @blur="markTouched('organizationName')"
    />

    <Checkbox v-model="organizationData.isPrivateOrganization" label="Particulier" />

    <FormInput
      v-show="!organizationData.isPrivateOrganization"
      v-model="organizationData.organizationNumber"
      label="Ondernemingsnummer"
      placeholder="KBO / ondernemingsnummer / BTW-nummer / EORI-nummer"
      :error="fieldError('organizationNumber')"
      :warning="fieldWarning('organizationNumber')"
      @blur="markTouched('organizationNumber')"
    />

    <FormInput
      v-model="organizationData.streetName"
      label="Straatnaam"
      placeholder="Straatnaam"
      :error="fieldError('streetName')"
      @blur="markTouched('streetName')"
    />

    <FormInput
      v-model="organizationData.houseNumber"
      label="Huisnummer"
      type="number"
      placeholder="12"
      :optional="true"
      :error="fieldError('houseNumber')"
      @blur="markTouched('houseNumber')"
    />

    <FormInput
      v-model="organizationData.houseNumberSuffix"
      label="Toevoeging"
      placeholder="A / bis / etc."
      :optional="true"
      :error="fieldError('houseNumberSuffix')"
      @blur="markTouched('houseNumberSuffix')"
    />

    <FormInput
      v-model="organizationData.postalCode"
      label="Postcode"
      placeholder="1234 AB"
      :error="fieldError('postalCode')"
      @blur="markTouched('postalCode')"
    />

    <FormInput
      v-model="organizationData.city"
      label="Stad"
      placeholder="Stad"
      :error="fieldError('city')"
      @blur="markTouched('city')"
    />

    <FormInput
      v-model="organizationData.country"
      label="Land"
      placeholder="Land"
      :error="fieldError('country')"
      @blur="markTouched('country')"
    />
    <template #footer="{ close }">
      <button class="btn btn-primary-outline" form="ignore-form" @click="close">Sluiten</button>
      <button class="btn btn-primary" form="ignore-form" @click="submitNewOrganisation">
        Opslaan
      </button>
    </template>
  </Modal>
</template>

<style scoped></style>
